/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { useCompany } from '@/hooks'

import { companyIdOf } from './entityGroups'

/**
 * A company reference — `102`, `'102'` or `company:sql102` — as the company's name, or undefined
 * when the viewer's company list does not hold it.
 *
 * Read from the companies the app already holds for the company picker rather than fetched: no
 * extra request, and nothing is revealed about a company the viewer has not been given. Callers
 * decide how to fall back, usually to the id.
 */
export const useCompanyName = (): ((raw: unknown) => string | undefined) => {
  const { companies } = useCompany()
  return React.useCallback(
    (raw: unknown) => {
      const id = companyIdOf(raw)
      return id === undefined ? undefined : companies.find((c) => c.id === id)?.name
    },
    [companies]
  )
}
